import { BaseOrderEntity, IBaseOrder, IBaseOrderEntity } from '@easy-lib/modules/order/oms/models/base-order.model';
import {
	EasyState,
	EasyValidityTypeCode,
	From,
	IOrderTrade,
	OmsOrderStates
} from '@easy-lib/modules/order/oms/models/orderModels.model';

import { IBaseEntity } from '@easy-lib/modules/shared/models/base.model';
import { commaSeparated } from '@easy-lib/tools/ag-grid.tool';
import {
	AggregateState,
	EasyStateClasses,
	OMSOrderAggregateStates,
	OMSOrderStateClasses,
	OrderConstants,
	OrderModelType,
	StateNotificationColorClasses,
	StateNotificationIconClasses,
	ValidForDeleteStates,
	ValidForEditStates,
	isOpenOrder
} from '@easy-lib/constants/order.constants';
import { StateNotificationColorClass, StateNotificationIconClass } from '@easy-lib/constants/orderStyles.constants';
import { DateHelper } from '@easy-lib/tools';

export class OMSOrder extends BaseOrderEntity<OMSOrder> {
	constructor(obj: Partial<OMSOrder>) {
		super(obj);
		Object.assign(this, obj);
		this.orderModelType = OrderModelType.oms;
	}
	orderFrom?: From;
	orderPlace?: number;
	hon?: number;
	parentId?: string;
	orderStateStr?: string;
	orderType?: number;
	isParentOfOnModifyBoard?: boolean;
	volumeAhead?: number;
	orderState: OmsOrderStates = OmsOrderStates.None;
	trades?: IOrderTrade[];
	get ExecutedQuantity() {
		let executed = 0;
		executed = this.trades?.reduce((sum, trade) => (trade.isCancel ? 0 : trade.quantity) + sum, 0) ?? 0;
		return executed;
	}
	get ExecutedPercent() {
		return Math.round((this.ExecutedQuantity / this.quantity) * 100);
	}
	get RemainingQuantity() {
		return (this.quantity ?? 0) - this.ExecutedQuantity;
	}
	get OrderExecutedLabel() {
		const value = (this.ExecutedQuantity * 100) / this.quantity;
		return `(${Math.round(value)}%) ` + commaSeparated(this.ExecutedQuantity);
	}

	get IsInErrorState() {
		return OMSOrderAggregateStates.get(this.orderState) === AggregateState.Error;
	}

	get IsSupervisorOrder(): boolean {
		return this.orderFrom === From.Supervisor;
	}

	get IsDeleteValid() {
		return (
			!this.IsSupervisorOrder &&
			this.easyState === EasyState.Done &&
			ValidForDeleteStates.includes(this.orderState)
		);
	}
	get IsEditValid(): boolean {
		return this.IsDeleteValid && ValidForEditStates.includes(this.orderState);
	}

	get IsOrderPartiallyExecuted(): boolean {
		return [
			OmsOrderStates.PartiallyExecutedAndCanceled,
			OmsOrderStates.PartiallyExecutedAndError,
			OmsOrderStates.PartiallyExecutedAndExpired,
			OmsOrderStates.PartiallyExecutedAndModified
		].includes(this.orderState);
	}

	get IsOrderValidToDate(): boolean {
		return [
			EasyValidityTypeCode.ValidToDate,
			EasyValidityTypeCode.Month,
			EasyValidityTypeCode.Week,
			EasyValidityTypeCode.ValidToCancellation
		].includes(this.validityType);
	}

	get StateClass() {
		if (this.easyState !== EasyState.Done) return EasyStateClasses.get(this.easyState);
		return OMSOrderStateClasses.get(this.AggregateState) ?? '';
	}

	showSupervisorSymbol = () =>
		this.IsSupervisorOrder || OMSOrderAggregateStates.get(this.orderState) === AggregateState.Supervisor;

	get ValidityDateTitle() {
		if (this.validityType === EasyValidityTypeCode.ValidToCancellation) return 'معتبر تا لغو';
		if (this.IsOrderValidToDate) return 'معتبر تا ' + DateHelper.getFormattedJDate(new Date(this.validityDate));
		return 'روز';
	}
	get IsSelectable() {
		return this.AggregateState !== AggregateState.Done;
	}

	get AggregateState() {
		return OMSOrderAggregateStates.get(this.orderState);
	}

	get StateNotificationClass() {
		return this.StateNotificationIconClass + ' ' + this.StateNotificationColorClass;
	}
	get StateNotificationColorClass() {
		if (![EasyState.Done, EasyState.WaitForDelete].includes(this.easyState))
			return StateNotificationColorClass.Waiting;
		return StateNotificationColorClasses.get(this.AggregateState) ?? '';
	}

	get StateNotificationIconClass() {
		if (![EasyState.Done, EasyState.WaitForDelete].includes(this.easyState))
			return StateNotificationIconClass.Waiting;
		return StateNotificationIconClasses.get(this.AggregateState) ?? '';
	}

	get StateNotificationText() {
		if (this.easyState !== EasyState.Done) return OrderConstants.EasyStateToText[this.easyState];
		return (
			OrderConstants.OrderStateToText[this.orderState] +
			(this.orderState === OmsOrderStates.Error ? `: ${this.errorDescription}` : '')
		);
	}

	get isOpenOrder() {
		return isOpenOrder.includes(this.orderState);
	}
}

export interface IOMSOrderGetRespons {
	orders: IBaseOrderEntity[];
	algoSuffixCharacters?: string;
}
export interface IOMSOrderUpdateRequest extends IBaseEntity, IBaseOrder {}
export interface IOMSOrderUpdateResponse extends IBaseEntity, IBaseOrder {}
export type IOMSOrderCreateRequest = IBaseOrder;
export interface IOMSOrderCreateResponse extends IBaseEntity, IBaseOrder {}
export type IOMSOrderDeleteRequest = IBaseEntity;
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IOMSOrderDeleteResponse {}
